import React from 'react';
import { useNavigate } from 'react-router-dom';
import { appUrl, visibleAppsFor, launchUrl } from '../config/apps';

function AppIcon({ appKey }) {
  const c = { width: 30, height: 30, viewBox: '0 0 24 24', fill: 'none', stroke: '#fff', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (appKey) {
    case 'devops':
      return <svg {...c}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>;
    case 'pm':
      return <svg {...c}><rect width="8" height="4" x="8" y="2" rx="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="M12 11h4"/><path d="M12 16h4"/><path d="M8 11h.01"/><path d="M8 16h.01"/></svg>;
    case 'support':
      return <svg {...c}><path d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5a9 9 0 0 1 18 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3"/></svg>;
    case 'dsp':
      return <svg {...c}><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>;
    default:
      return <svg {...c}><rect width="18" height="18" x="3" y="3" rx="2"/></svg>;
  }
}

export default function Launcher() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const openApp = (app) => {
    if (!app.enabled) return;
    // Products card: open the in-portal products list rather than redirecting.
    if (app.productsCard) {
      navigate('/products');
      return;
    }
    // External apps (not CLTS internal): just open in a new tab, no SSO token handoff.
    if (app.external) {
      window.open(appUrl(app), '_blank', 'noopener,noreferrer');
      return;
    }
    // Token-passing SSO: hand the JWT + user payload to the target app's /sso route.
    window.location.href = launchUrl(app, token, user);
  };

  // Filter tiles by the user's app allowlist (see config/apps.js).
  const visibleApps = visibleAppsFor(user);

  return (
    <div style={{ minHeight: '100vh', background: '#ffffff', color: '#0f172a' }}>
      {/* Top bar */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 28px', borderBottom: '1px solid #e5e7eb', background: '#ffffff'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>🚀</span>
          <span style={{ fontSize: 17, fontWeight: 600, color: '#0f172a' }}>CLTS Apps</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{user.fullName || user.username}</div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>{user.role}</div>
          </div>
          <div style={{
            width: 34, height: 34, borderRadius: '50%', background: '#eef2ff', color: '#4f46e5',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600
          }}>{(user.fullName || user.username || '?').slice(0, 2).toUpperCase()}</div>
          <button onClick={logout} style={{
            padding: '7px 14px', background: '#f8fafc', color: '#475569',
            border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, cursor: 'pointer'
          }}>Logout</button>
        </div>
      </div>

      {/* Greeting */}
      <div style={{ textAlign: 'center', padding: '44px 20px 28px' }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, color: '#0f172a' }}>Welcome, {user.fullName || user.username} 👋</h1>
        <p style={{ fontSize: 14, color: '#64748b', marginTop: 6 }}>Choose an app to continue</p>
      </div>

      {/* Launcher grid */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 150px))',
        gap: 8, justifyContent: 'center', padding: '0 24px 60px', maxWidth: 680, margin: '0 auto'
      }}>
        {visibleApps.map(app => (
          <div key={app.key}
            onClick={() => openApp(app)}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
              padding: '20px 8px', borderRadius: 14, position: 'relative',
              cursor: app.enabled ? 'pointer' : 'not-allowed',
              opacity: app.enabled ? 1 : 0.5, transition: 'background 0.15s'
            }}
            onMouseEnter={e => { if (app.enabled) e.currentTarget.style.background = '#f8fafc'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <div style={{
              width: 60, height: 60, borderRadius: 16,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: app.enabled ? app.color : '#cbd5e1',
              boxShadow: app.enabled ? `0 4px 12px ${app.color}40` : 'none'
            }}><AppIcon appKey={app.key} /></div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', textAlign: 'center', display: 'flex', alignItems: 'center', gap: 3 }}>
              {app.name}
              {app.enabled && app.external && <span style={{ fontSize: 11, color: '#94a3b8' }}>↗</span>}
            </div>
            {!app.enabled && (
              <span style={{
                position: 'absolute', top: 8, right: 8, fontSize: 9, fontWeight: 600,
                background: '#f1f5f9', color: '#64748b', padding: '2px 7px', borderRadius: 20
              }}>Soon</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
